import { Dialog, Switch } from '@headlessui/react'
import { useSnapshot } from 'valtio'
import classNames from 'classnames'
import { type ReactElement } from 'react'
import { proxyNewUser } from '../../../../shared/proxies/newUserProxy'
import { PlaceholderBox } from '../../../../shared/components/ui/PlaceholderBox'
import TextInput from '../../../../shared/components/ui/forms/TextInput'
import { proxyGlobalAuth } from '../../../../shared/proxies/proxyGlobalAuth'
import { ErrorDisplay } from '../../../../shared/components/ui/forms/ErrorDisplay'
import { handleEditSubmit, closeEditModal } from './utils'
import { FormFieldNamesEnum } from './NewUserForm/formFieldNamesEnum'
import { handleBlur, handleChange, handleEmailBlur, onCheckboxChange } from './NewUserForm/utils'

export function EditUserModal (): ReactElement {
  const { allowSubmit, displayUserEditModal, emailValidationCall, formErrors, formValues, showEmailError, showLastNameError, showFirstNameError } = useSnapshot(proxyNewUser)
  const { siteTheme: { toggleButtonColors, buttonColors } } = useSnapshot(proxyGlobalAuth)

  return (
    <Dialog
      open={displayUserEditModal}
      onClose={() => { closeEditModal() }}
      className="fixed z-10 inset-0 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

        <div className="flex justify-center mt-12">
          <PlaceholderBox loading={false} rows={20} additionalClasses="p-2">
            <form className="bg-white rounded-lg py-6 px-8 border w-full flex flex-col " onSubmit={handleEditSubmit}>
              <h3 className="font-medium text-xl mb-4 mx-auto">Edit User Account</h3>
              {emailValidationCall.error && <ErrorDisplay error={emailValidationCall.error} />}
              <div className="w-full">
                <TextInput
                  defaultValue={formValues[FormFieldNamesEnum.Email] as string}
                  error={formErrors[FormFieldNamesEnum.Email]}
                  handleBlur={handleEmailBlur(FormFieldNamesEnum.Email)}
                  handleChange={handleChange(FormFieldNamesEnum.Email)}
                  placeholder="Email"
                  required
                  showError={showEmailError}
                  type="email"
                />
                <div className="flex space-x-4">
                  <div className="w-full md:w-1/2">
                    <TextInput
                      defaultValue={formValues[FormFieldNamesEnum.FirstName] as string}
                      error={formErrors[FormFieldNamesEnum.FirstName]}
                      handleBlur={handleBlur(FormFieldNamesEnum.FirstName)}
                      handleChange={handleChange(FormFieldNamesEnum.FirstName)}
                      placeholder="First Name"
                      required
                      showError={showFirstNameError}
                      type="text"
                    />
                  </div>
                  <div className="w-full md:w-1/2">
                    <TextInput
                      defaultValue={formValues[FormFieldNamesEnum.LastName] as string}
                      error={formErrors[FormFieldNamesEnum.LastName]}
                      handleBlur={handleBlur(FormFieldNamesEnum.LastName)}
                      handleChange={handleChange(FormFieldNamesEnum.LastName)}
                      placeholder="Last Name"
                      required
                      showError={showLastNameError}
                      type="text"
                    />
                  </div>
                </div>
                <div className="w-full">
                  <Switch.Group as="div" className="flex items-center justify-between">
                    <span className="flex flex-grow flex-col">
                      <Switch.Label as="span" className="text-sm font-medium text-gray-900" passive>
                        User is Admin
                      </Switch.Label>
                    </span>
                    <Switch
                      checked={!!formValues[FormFieldNamesEnum.IsAdmin]}
                      onChange={onCheckboxChange}
                      className={classNames(
                        formValues[FormFieldNamesEnum.IsAdmin] ? toggleButtonColors.activeClasses : toggleButtonColors.inactiveClasses,
                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out', toggleButtonColors.focusClasses
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={classNames(
                          formValues[FormFieldNamesEnum.IsAdmin] ? 'translate-x-5' : 'translate-x-0',
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                        )}
                      />
                    </Switch>
                  </Switch.Group>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  className="w-36 flex justify-center py-2 px-4 rounded-md text-sm font-medium text-wpy-blue-500 hover:text-wpy-blue-700 active:bg-wpy-blue-200 focus:outline-none"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button
                  disabled={!allowSubmit}
                  type="submit"
                  className={classNames(buttonColors.classes, 'w-48 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white')}
                >
                  Confirm
                </button>
              </div>
            </form>
          </PlaceholderBox>
        </div>
      </div>
    </Dialog>
  )
}
